<script setup lang='ts'>
import WisionLogo from '../WisionLogo.vue';
import Button from '../Button.vue';
import { useRouter } from 'vue-router';
import { useAppState } from '../../store/state';
import CustomInput from '../CustomInput.vue';
import { ref } from 'vue';
import useFieldValidation from '../../utilities/useFieldValidation';
import { validateEmail } from '../../utilities/utils';
import ScrollContainer from '../ScrollContainer.vue';
import { event, bootstrap } from 'vue-gtag';

const state = useAppState();
const router = useRouter();
const fieldValidation = useFieldValidation();

const name = ref<string | undefined>(undefined);
const email = ref<string | undefined>(undefined);
const confirmEmail = ref<string | undefined>(undefined);
const phone = ref<string | undefined>(undefined);
const password = ref<string | undefined>(undefined);
const confirmPassword = ref<string | undefined>(undefined);
const userExists = ref(false);
const isLoading = ref(false);
const errorFetchingUser = ref(false);

const noAccess = ref(false);

const nameValid = fieldValidation.validation(name);
const emailValid = fieldValidation.validation(email, (value: unknown) => validateEmail(value as string));
const emailConfirmValid = fieldValidation.validation(confirmEmail, () => confirmEmail.value === email.value);
const phoneValid = fieldValidation.validation(phone);
const passwordValid = fieldValidation.validation(password, (val: unknown) => (val as string).length > 5);
const confirmPasswordValid = fieldValidation.validation(confirmPassword, () => confirmPassword.value === password.value);

state.logout();
const tag = state.configTag;
if (tag?.config.organization_id) {
  noAccess.value = true;
  router.push({ name: 'no-access' });
}

const handleDone = async () => {
  fieldValidation.validateAll();
  if (!fieldValidation.allFieldsValid.value) return;
  isLoading.value = true;
  errorFetchingUser.value = false;

  const [res, error] = await state.userExists(state.configTag?.uuid as string, email.value as string);
  userExists.value = res?.userFound;

  if (!userExists.value && !error) {
    state.setCreatedUser({
      name: name.value as string,
      email: email.value?.toLocaleLowerCase()as string,
      phone: phone.value as string,
      password: password.value as string
    });

    await bootstrap();
    event('user_created', { method: 'Google' });

    router.push({ name: 'connect' });
  }

  errorFetchingUser.value = error;
  isLoading.value = false;
};
</script>

<template>
  <div  class="flex h-full flex-col items-center w-full text-onboard-shade2">
    <ScrollContainer v-if="!noAccess">
      <div class="flex flex-col mt-[20%] items-center">
        <WisionLogo :width="114" class="text-onboard-foreground"/>

        <span class="text-xl text-center mt-3 uppercase">
          {{ $t('nfc.createAccount') }}
        </span>

        <div class="text-center mt-2 leading-none">
          <span class="text-sm uppercase">
            {{ $t('nfc.provideNameAndEmail') }}
          </span>
        </div>
      </div>

      <div class="w-full mt-6">
        <CustomInput type="email" :valid="emailValid" class="mt-3 h-12" :placeholder="$t('nfc.form.enterEmail').toUpperCase()" v-model="email" />
        <CustomInput type="email" :valid="emailConfirmValid" class="mt-3 h-12 mb-2" :placeholder="$t('nfc.form.confirmEmail').toUpperCase()" v-model="confirmEmail" />

        <CustomInput :valid="nameValid" class="h-12" :placeholder="$t('nfc.form.enterName').toUpperCase()" v-model="name" />
        <CustomInput type="tel" :valid="phoneValid" class="mt-3 h-12" :placeholder="$t('nfc.form.enterPhone').toUpperCase()" v-model="phone" />

        <CustomInput type="password" :valid="passwordValid" class="mt-6 h-12" :placeholder="$t('nfc.form.enterPassword').toUpperCase()" v-model="password" />
        <p v-if="!passwordValid" class="text-red-500">{{ $t('nfc.form.passwordMinChar') }}</p>
        <CustomInput type="password" :valid="confirmPasswordValid" class="mt-3 h-12" :placeholder="$t('common.confirmPassword').toUpperCase()" v-model="confirmPassword" />
        <p v-if="!confirmPasswordValid" class="text-red-500">{{ $t('nfc.form.passwordDontMatch') }}</p>

        <Button
          class="mt-6 h-12 border-8 uppercase" 
          @click="handleDone()"
          :is-loading="isLoading"
        >
          {{ $t('common.done') }}
        </Button>
        <p v-if="errorFetchingUser" class="text-red-500">
          {{ $t('nfc.somethingWrong') }}</p>
      </div>

      <div class="flex flex-col items-center mt-4">

        <span class="text-xl mb-1">
          {{ $t('nfc.terms.cookiesAndTerms') }}
        </span>

        <div class="text-center leading-none uppercase">
          <span class="text-sm">
            {{ $t('nfc.terms.acceptOnDone') }}
            <a href="/doc/terms.pdf" class="underline" target="_blank" rel="noopener noreferrer">
              {{ $t('nfc.terms.pressHere') }}
            </a></span>
        </div>

      </div>
    </ScrollContainer>
    <span class="text-onboard-shade1 pt-5 pb-3 mt-auto text-center">WISION BUILD {{ state.build }}</span>

    <transition
      appear
      enter-active-class="transition-opacity ease-out"
      leave-active-class="transition-opacity ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div 
        v-if="userExists"
        class="fixed max-w-3xl w-[80%] bg-onboard-background shadow-xl top-[20vh] text-center py-20 px-10 flex flex-col items-center"
      >
        <p>
          {{ $t('nfc.emailExists') }}
        </p>
        <p>
          {{ $t('nfc.differentEmail') }}
        </p>
        <div class="w-50 flex flex-col gap-2 max-w-xl items-center">
          <Button class="!w-[150px] mt-6 h-12 border-8" @click="userExists = false">
            {{ $t('common.cancel') }}
          </Button>
          <Button class="!w-[150px] h-12 border-8" @click="() => router.replace({ name: 'login' })">
            {{ $t('nfc.form.login') }}
          </Button>
        </div>
      </div>
    </transition>
  </div>
</template>

